import { computed, ref } from 'vue'
import { createGlobalState } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { getFeedbackTypes } from '@/api/service'
import type { FeedbackTypeDTO, FormattedFeedbackTypeDTO } from '@/types/service'

/**
 * 反馈类型数据 Hook
 * 用于获取和格式化反馈类型数据，包含本地化文本
 */
export const useFeedbackTypes = createGlobalState(() => {
  const { t } = useI18n()

  // ===== 状态 =====
  const rawFeedbackTypes = ref<FeedbackTypeDTO[]>([])
  const isLoading = ref(false)
  const error = ref<Error | null>(null)

  // ===== 本地化映射 =====
  const feedbackTypeMapping: Record<number, string> = {
    1: '存款问题',
    2: '提现问题',
    3: '意见建议',
    4: '游戏问题',
    5: '登录问题',
    6: '修改信息',
    7: '流水问题'
  }

  const feedbackTypes = computed<FormattedFeedbackTypeDTO[]>(() =>
    rawFeedbackTypes.value.map((item) => {
      const localeKey = feedbackTypeMapping[item.objKey ?? 0]

      return {
        ...item,
        feedbackTypeText: localeKey ? t(localeKey) : item.objValue || ''
      }
    })
  )

  // ===== 方法 =====

  /**
   * 获取反馈类型列表（格式化后）
   * 返回包含 feedbackTypeText 的格式化数据，可直接用于下拉框渲染
   */
  const fetchFeedbackTypes = async () => {
    isLoading.value = true
    error.value = null

    try {
      const res = await getFeedbackTypes()
      rawFeedbackTypes.value = res.data || []
    } catch (err) {
      console.error('Failed to fetch feedback types:', err)
      error.value = err as Error
      rawFeedbackTypes.value = []
    } finally {
      isLoading.value = false
    }
  }

  /**
   * 根据 objKey 获取反馈类型文本
   * 用于列表和详情显示
   */
  const getFeedbackTypeText = (objKey?: number): string => {
    if (!objKey) return ''
    const type = feedbackTypes.value.find((t) => t.objKey === objKey)
    return type?.feedbackTypeText || ''
  }

  // ===== 导出 =====
  return {
    // 状态
    feedbackTypes,
    isLoading,
    error,

    // 方法
    fetchFeedbackTypes,
    getFeedbackTypeText
  }
})

export type UseFeedbackTypesReturn = ReturnType<typeof useFeedbackTypes>
